import { loadOptions } from '../formset.js';
import { REGEX, replaceIndex } from '../table.js';
import db from '../db.js';
import api from '../../js/api.js';
import { fetchInputCancelReasonsSilent } from '../../js/utils/input-cancel-reason.js';

function warehousesActiveOnly(rows) {
	return (Array.isArray(rows) ? rows : []).filter(
		(r) => !(r.is_disabled === true || r.is_disabled === 1 || r.is_disabled === '1')
	);
}

function parseWarehouseId(raw) {
	if (raw == null || raw === '' || raw === -1 || raw === '-1')
		return null;
	// UUID de empresa não é armazém
	if (typeof raw === 'string' && /[a-f]/i.test(raw) && raw.includes('-'))
		return null;
	const n = Number(raw);
	return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Exportado para testes — monta filtro/range da lista de posições na Saída. */
export function resolveOutputLocationsScope(companyId, headerWarehouseId) {
	const c = companyId;
	const wh = parseWarehouseId(headerWarehouseId);
	if (c == null || c === '')
		return { ok: false, companyId: null, warehouseId: null };
	return { ok: true, companyId: c, warehouseId: wh };
}

// Requires observer.js
export default function (...args)	// store, { value: '', labels: [], separator: '' }, extra_key, range
{
	
	let SHARED = { _this: null };	// Create a new object each time the function is called, ie when the type is being defined

	return {

		_store: args[0],									// DB store name (eg: 'companies')
		_value: args[1]?.value || 'id',						// Property that will become the <option value=""> (eg: 'id')
		_labels: args[1]?.labels || ['name'],				// Properties that will become the <option>label</option> (eg: ['name'])
		_separator: args[1]?.sep,							// Optional character to join array-like label values (eg: '-')
		_filter: args[1]?.filter,							// Optional filter function (record) => boolean
		_key: args[1]?.key,									// Optional extra key to list the db data (eg: 'company_id')
		_range: args[1]?.range,								// Index for the range used to retrieve db data (0=upper/lower bound, 1=single value)
		_index: args[1]?.index,								// Number of index to use when retrieving db data (1 or 2)
		/** Saídas multi-armazém: id de posição não é global — value = `warehouse_id:location_id`, label com prefixo do armazém */
		_compositeWhLoc: Boolean(args[1]?.compositeWhLoc),

		async actions(ev, publisher, key)
		{
			const fmtLocAddr = (a) => (Array.isArray(a) ? a.join('-') : String(a ?? ''));
			const resolveHeaderWarehouseId = () =>
			{
				try
				{
					const fromField = parseWarehouseId(this.locate?.('warehouse_id')?.getValue?.());
					if (fromField != null)
						return fromField;
				}
				catch (_) { /* noop */ }
				// Se o publisher for o próprio armazém do cabeçalho, use o valor publicado
				try
				{
					const pubId = String(publisher?.getId?.() || '');
					if (/\bwarehouse_id\b/.test(pubId) && !/warehouse_company_id/.test(pubId))
						return parseWarehouseId(key);
				}
				catch (_) { /* noop */ }
				return null;
			};
			const action = async (keys) =>
			{
				const headerWh = resolveHeaderWarehouseId();
				const companyId = keys[0];
				const MAX_ID = 2147483647;
				/** Resposta da API — fonte preferencial da datalist (IDB pode falhar/esvaziar). */
				let apiLocs = null;

				// Hidratar IDB a partir da API: no Inserir o store de locations costuma estar
				// vazio ou só com outro armazém — filtrar WH3 sem buscar na API = lista vazia.
				if (this._compositeWhLoc && this._store === 'locations' && companyId != null && companyId !== '')
				{
					try
					{
						const parts = [`company_id*eq*${companyId}`];
						if (headerWh != null)
							parts.push(`warehouse_id*eq*${headerWh}`);
						const qs = new URLSearchParams({
							filter: parts.join(','),
							sort: '4',
							limit: '0',
						});
						const locs = await api.call(api.list_locations, `?${qs.toString()}`);
						if (Array.isArray(locs))
						{
							apiLocs = locs;
							await db.set('locations', locs);
						}
					}
					catch (_)
					{
						/* mantém IDB atual se a API falhar */
					}
				}

				// #MT-6BF7122 — Motivos cancelamento Entrada: IDB costuma vazio até abrir o catálogo.
				// Fetch silencioso — api.call abriria «Carregando...» e travava o formulário.
				let apiCancelReasons = null;
				if (
					this._store === 'input_cancel_reasons'
					&& typeof companyId === 'string'
					&& companyId.includes('-')
				)
				{
					try
					{
						const rows = await fetchInputCancelReasonsSilent(companyId, false);
						if (Array.isArray(rows) && rows.length)
						{
							apiCancelReasons = rows.filter((r) => !r.is_disabled);
							await db.set('input_cancel_reasons', rows);
						}
					}
					catch (_)
					{
						/* mantém IDB se API falhar (ex.: sem permissão GET) */
					}
				}

				let range;
				if (this._compositeWhLoc && this._store === 'locations' && keys.length === 1)
				{
					const c = keys[0];
					if (headerWh != null)
						range = IDBKeyRange.bound([c, headerWh, 0], [c, headerWh, MAX_ID]);
					else
					{
						const MAX_WH = 2147483647;
						range = IDBKeyRange.bound([c, 0, 0], [c, MAX_WH, MAX_ID]);
					}
				}
				else
				{
					let base_key = this._key ? [''] : '';
					let top_key = this._key ? ['\uffff'] : '\uffff';
					range = this._range ? IDBKeyRange.only([...keys]) : IDBKeyRange.bound([...keys, base_key], [...keys, top_key]);
				}
				const idbIndex = (this._compositeWhLoc && this._store === 'locations')
					? 1
					: (this._index ?? 2);
				let data = await db.list(this._store, range, idbIndex);
				if (this._store === 'input_cancel_reasons' && Array.isArray(apiCancelReasons))
					data = apiCancelReasons;
				if (this._store === 'warehouses')
					data = warehousesActiveOnly(data);
				// Com armazém do cabeçalho já escolhido, não filtrar por catálogo de warehouses
				// no IDB (catálogo incompleto/race → lista de Posição vazia).
				if (this._store === 'locations' && data && data.length && !(this._compositeWhLoc && headerWh != null)) {
					try {
						const whsRaw = await db.list('warehouses').catch(() => []);
						const activeWhKeys = new Set(
							warehousesActiveOnly(whsRaw).map((w) => `${w.company_id}:${w.id}`)
						);
						if (activeWhKeys.size > 0)
							data = data.filter((l) => activeWhKeys.has(`${l.company_id}:${l.warehouse_id}`));
					} catch (_) { /* noop */ }
				}
				// Com hydrate OK, montar datalist a partir da API (evita IDB vazio/range/tipo).
				if (this._compositeWhLoc && this._store === 'locations' && Array.isArray(apiLocs))
				{
					data = headerWh != null
						? apiLocs.filter((l) => Number(l.warehouse_id) === headerWh)
						: apiLocs.slice();
				}
				if (this._compositeWhLoc && this._store === 'locations' && headerWh != null)
					data = (data || []).filter((l) => Number(l.warehouse_id) === headerWh);
				if (this._filter) data = (data || []).filter(this._filter);
				if (this._compositeWhLoc && this._store === 'locations')
				{
					const companyIdForSel = keys[0];
					let selVal = '';
					try {
						selVal = String(this.getValue?.() ?? '').trim();
					} catch (_) { /* noop */ }
					if (!selVal) {
						const el = document.querySelector(this.selector);
						if (el)
							selVal = String(el.value || '').trim();
					}
					const parsed = /^(\d+):(\d+)$/.exec(selVal);
					if (parsed && companyIdForSel != null && companyIdForSel !== '')
					{
						const wh = Number(parsed[1]);
						const lid = Number(parsed[2]);
						if (headerWh == null || wh === headerWh)
						{
							const exists = (data || []).some((loc) =>
								String(loc.company_id) === String(companyIdForSel)
								&& Number(loc.warehouse_id) === wh
								&& Number(loc.id) === lid);
							if (!exists)
							{
								try {
									const one = await db.get('locations', [companyIdForSel, wh, lid]);
									if (one)
										data = [...(data || []), one];
								} catch (_) { /* IDB indisponível */ }
							}
						}
					}
					data = (data || []).map((loc) => ({
						...loc,
						_wh_loc: `${loc.warehouse_id}:${loc.id}`,
						_loc_label: headerWh != null
							? fmtLocAddr(loc.address)
							: `${loc.warehouse_id} · ${fmtLocAddr(loc.address)}`,
					}));
					// Garantia: nenhum endereço de outro armazém na lista
					if (headerWh != null)
					{
						const leaked = data.filter((l) => Number(l.warehouse_id) !== headerWh);
						if (leaked.length)
							data = data.filter((l) => Number(l.warehouse_id) === headerWh);
					}
					loadOptions(this.selector, data, '_wh_loc', ['_loc_label']);
				}
				else
					loadOptions(this.selector, data, this._value, this._labels, this._separator);
				this.refresh();
			};
			let keys = [!isNaN(key) && !Array.isArray(key) ? Number(key) : key];
			if (this._key)
			{
				if (this.getId().match(REGEX) && this._key.match(REGEX))
					this._key = replaceIndex(this._key, this.getId().match(REGEX).pop());
				let val = this.locate(this._key)?.getValue();
				keys.unshift(typeof val === 'string' && /^\d+$/.test(val) ? Number(val) : val);
			}
			if (this._compositeWhLoc && this._store === 'locations' && this._key && keys.length >= 1)
			{
				const companyVal = keys[0];
				keys = companyVal != null && companyVal !== '' ? [companyVal] : [];
			}
			if (keys.length && keys.every(k => k != null && k !== '' && (typeof k === 'string' || (typeof k === 'number' && !isNaN(k) && k >= 0))))
				this.preventMultiCalls(SHARED, action, () => this.refresh(), keys);
		}

	};

}
